import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { ApiConstants } from '../../constants/api-constants';
import { HttpClient, HttpContext } from '@angular/common/http';
import { AccountAddressModel, AccountModel, ContactsModel, FrontendUserAddressModel, PaginationModel, RequestParamModel, TradeTypeModel } from '@coreModels';
import { ToastService } from '../toast.service';
import { SKIP_ERROR_TOAST } from '../../interceptors/http-context.tokens';
import { AddressDraft, AddressTypeLink, AddressTypeTable } from '../../utils/address-type';

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  constructor(private http: HttpClient,
    private toastService: ToastService
  ) { }

  /**
   * This method is used to get the list of buissness role
   * @param requestParamModel
   * @returns
   */
  getBusinessRoleList(requestParamModel: RequestParamModel): any {
    return this.http.get(ApiConstants.ACCOUNT_BUSINESS_ROLE_LIST_URL + requestParamModel?.getParams(), { observe: 'response' })
      .pipe(map((response: any) => {
        const responseList = new PaginationModel<TradeTypeModel>();
        const headers = response.headers;
        responseList.setHeaders(headers);
        responseList.data = response.body.data as TradeTypeModel[];
        return responseList;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /**
   * This method is used to get list of trade types
   * @param requestParamModel
   * @returns
   */
  getTradeTypeLists(requestParamModel: RequestParamModel): any {
    return this.http.get(ApiConstants.ACCOUNT_TRADE_TYPE_LIST_URL + requestParamModel?.getParams(), { observe: 'response' })
      .pipe(map((response: any) => {
        const responseList = new PaginationModel<TradeTypeModel>();
        const headers = response.headers;
        responseList.setHeaders(headers);
        responseList.data = response.body.data as TradeTypeModel[];
        return responseList;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /**
   * This method is used to get account by abn
   * @param requestModel
   * @returns
   */
  getAccountByABN(requestModel: Partial<AccountModel>): any {
    return this.http.post(ApiConstants.ACCOUNT_BY_ABN_URL, requestModel)
      .pipe(map((response: any) => {
        return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }

  /**
  * @param accountId - The ID of the account to retrieve details for.
  * @param requestParamModel - A model containing request parameters to be sent with the HTTP request.
  * @returns An Observable that emits the AccountModel data on success.
  */
  getAccountDetails(accountId: number, requestParamModel: RequestParamModel): Observable<AccountModel> {
    return this.http.get(`${ApiConstants.ACCOUNT_DETAIL_URL}${accountId}${requestParamModel?.getParams()}`)
      .pipe(map((response: any) => {
        return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
   }

  /**
   * This method is used to get the list of trade account
   * @param requestParamModel
   * @returns
   */
  getCompanies(requestParamModel: RequestParamModel): any {
    return this.http.get(ApiConstants.ACCOUNT_LIST_URL + requestParamModel?.getParams(), { observe: 'response' })
      .pipe(map((response: any) => {
        const responseList = new PaginationModel<AccountModel>();
        const headers = response.headers;
        responseList.setHeaders(headers);
        responseList.data = response.body.data as AccountModel[];
        return responseList;
      }), catchError((err: unknown) => {
     this.toastService.showError(err)
        return throwError(() => err);
      }));
  }

  getUserDetails(requestParamModel: RequestParamModel): any {
    return this.http.get(ApiConstants.CONTACT_LIST_URL + requestParamModel?.getParams()).pipe(map((response: any) => {
      return response.data || response.body.data;
    }), catchError((err: unknown) => {
      this.toastService.showError(err)
      return throwError(() => err);
    }));
  }

  getSettingData(requestParamModel?: RequestParamModel): Observable<any> {
    return this.http.get(ApiConstants.SETTINGS_URL + requestParamModel?.getParams()).pipe(map((response: any) => {
      return response.data || response.body.data;
    }), catchError((err: unknown) => {
      this.toastService.showError(err)
      return throwError(() => err);
    }));
}

getContactDetail(accountId: number, requestParamModel: RequestParamModel): Observable<ContactsModel> {
  return this.http.get(`${ApiConstants.CONTACT_DETAIL_URL}${accountId}${requestParamModel?.getParams()}`)
    .pipe(map((response: any) => {
      return response.data;
    }), catchError((err: unknown) => {
      return throwError(() => err);
    }));
}

  /**
   * This method is used to get account by abn
   * @param requestModel
   * @returns
   */
  updateHomeAddress(requestModel: any): Observable<number>  {
    return this.http.patch(ApiConstants.USER_UPDATE_ADDRESS_URL + '/' + requestModel.id, requestModel)
      .pipe(map((response: any) => {
        return response.data;
      }), catchError((err: unknown) => {
        return throwError(() => err);
      }));
  }


  setCompanyAddressAsDefault(requestModel: any): Observable<number> {
    return this.http.post(ApiConstants.CONTACT_SET_COMPANY_ADDRESS_AS_DEFAULT_URL, requestModel)
    .pipe(map((response: any) => {
      return response.data;
    }), catchError((err: unknown) => {
      return throwError(() => err);
    }));
  }

updateAccountAddress(accountAddressModel: any): Observable<number>  {
  return this.http.patch(ApiConstants.ACCOUNT_UPDATE_ADDRESS_URL  + accountAddressModel.id, accountAddressModel)
    .pipe(map((response: any) => {
      return response.data;
    }), catchError((err: unknown) => {
      return throwError(() => err);
    }));
}

/**
 * The Residential / Business / PO Box / Unknown an address would get, by the same check a save
 * runs - saved nowhere. Quiet (no error toast): it backs the address popup's Address Type field,
 * and the save that follows stores the real answer. The API takes the owner from the session or,
 * with `link` (a shared quote / delivery link, no session), from that link's quote; on sign-up
 * (`link.signup`) there is no owner yet.
 */
previewAddressType(table: AddressTypeTable, draft: AddressDraft, link?: AddressTypeLink | null): Observable<string | null> {
  const url = link?.exportId ? ApiConstants.ADDRESS_TYPE_PREVIEW_BY_EXPORT_ID_URL + encodeURIComponent(String(link.exportId))
    : link?.trackingId ? ApiConstants.ADDRESS_TYPE_PREVIEW_BY_TRACK_ID_URL + encodeURIComponent(String(link.trackingId))
      : link?.signup ? ApiConstants.ADDRESS_TYPE_PREVIEW_FOR_SIGNUP_URL
        : ApiConstants.ADDRESS_TYPE_PREVIEW_URL;
  const body = {
    table,
    address_line_1: draft.address_line_1 ?? '',
    address_line_2: draft.address_line_2 ?? null,
    suburb: draft.suburb ?? null,
    town_city: draft.town_city ?? draft.city ?? null,
    state_region: draft.state_region ?? draft.region ?? null,
    // A form may hold the post code as a number (sign-up fills it from a saved address with `+`); the API trims it as text.
    post_code: draft.post_code != null ? String(draft.post_code) : null,
    country: draft.country ?? null,
  };
  return this.http.post(url, body, {context: new HttpContext().set(SKIP_ERROR_TOAST, true)})
    .pipe(map((response: any) => response?.data?.address_type ?? null), catchError((err: unknown) => {
      return throwError(() => err);
    }));
}

updateAccountDetails(accountModel: AccountModel): Observable<AccountModel> {
  return this.http.patch(ApiConstants.ACCOUNT_UPDATE_DETAIL_URL + accountModel.id, accountModel).pipe(map<any, AccountModel>(response => {
    accountModel = response.data as AccountModel;
    return accountModel;
  }),
   catchError((err: unknown) => {
    return throwError(() => err);
  }));
}

updateUserAddressDetails(frontendUserAddressModel: any): Observable<FrontendUserAddressModel> {
  return this.http.post(ApiConstants.USER_UPDATE_ADDRESS_URL, frontendUserAddressModel).pipe(map<any, FrontendUserAddressModel>(response => {
      let userModel = response.data as FrontendUserAddressModel;
      return userModel;
    }),
    catchError((err: unknown) => {
      return throwError(() => err);
    }));
}

updateAccountAddressDetails(accountAddressModel: any): Observable<AccountAddressModel> {
  return this.http.post('account-address', accountAddressModel).pipe(map<any, AccountAddressModel>(response => {
      let accountModel = response.data as AccountAddressModel;
      return accountModel;
    }),
    catchError((err: unknown) => {
      return throwError(() => err);
    }));
}


uploadLogo(_id: any, body: any) {
  return this.http.post(ApiConstants.ACCOUNT_UPDATE_LOGO_URL + _id, body)
    .pipe(map<any, any>(response => {
      return response.data;
    }),
    catchError((err: unknown) => {
      return throwError(() => err);
    }));
}
}




